import React from 'react';

import { ApiErrors } from '@betterment/js-runtime';
import { BoxProps, ErrorText, FormikTextInput, InputGroup, Label } from '@betterment/style-closet-react';
import { ErrorMessage, FormikHelpers, FormikValues } from 'formik';
import isFunction from 'lodash/isFunction';
import _lowerCase from 'lodash/lowerCase';
import _reduce from 'lodash/reduce';
import _upperFirst from 'lodash/upperFirst';
import * as Yup from 'yup';

interface InputGroupWithLabelAndErrorProps extends Omit<BoxProps, 'children'> {
  fieldName: string;
  labelText?: string;
  children?: React.ReactNode | ((inputProps: { id: string; name: string; mb: string }) => React.ReactNode);
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  component?: React.ComponentType<React.PropsWithChildren<{ id: string; name: string }>>;
  ariaLabel?: string;
}

export const InputGroupWithLabelAndError: React.FC<InputGroupWithLabelAndErrorProps> = ({
  fieldName,
  labelText,
  children,
  placeholder = '',
  component: Component = FormikTextInput,
  disabled = false,
  autoFocus = false,
  ariaLabel,
  ...boxProps
}) => {
  const commonInputProps = { id: fieldName, name: fieldName, mb: '3' };
  if (children && isFunction(children)) {
    children = children(commonInputProps);
  }

  return (
    <InputGroup {...boxProps}>
      {labelText && <Label htmlFor={fieldName}>{labelText}</Label>}
      {children || (
        <Component
          {...commonInputProps}
          aria-label={ariaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
          placeholder={placeholder}
        />
      )}
      <ErrorMessage name={fieldName} component={ErrorText} />
    </InputGroup>
  );
};

export type PickedFormikHelpers<T> = Pick<FormikHelpers<T>, 'setFieldTouched' | 'setFieldError'>;

export const applyApiErrorsToFormik = <Values extends FormikValues = FormikValues>(
  formik: PickedFormikHelpers<Values>,
  apiErrors: ApiErrors,
  options?: {
    applyMessageOnlyForKey?: string;
  }
) => {
  Object.keys(apiErrors.messages).forEach(key => {
    let error = '';
    if (options?.applyMessageOnlyForKey || key === 'base') {
      error = `${apiErrors.messages[key][0]}.`;
    } else {
      error = `${_upperFirst(_lowerCase(key))} ${apiErrors.messages[key][0]}.`;
    }
    void formik.setFieldTouched(key, true, false);
    void formik.setFieldError(key, error);
  });
};

export function buildErrorMessagesFromApiErrors(
  this: Yup.TestContext,
  apiErrors: ApiErrors,
  fieldPrefix?: string
): Yup.ValidationError[] {
  return _reduce<ApiErrors['messages'], Yup.ValidationError[]>(
    apiErrors.messages,
    (result, value, key) => {
      const path = fieldPrefix ? `${fieldPrefix}.${key}` : key;
      return result.concat(this.createError({ path, message: `${_upperFirst(_lowerCase(key))} ${value[0]}` }));
    },
    []
  );
}

export function createFieldValidator<FieldValue>(
  name: string,
  validator: Yup.Schema<FieldValue>,
  validateField: FormikHelpers<FormikValues>['validateField']
) {
  return {
    validate: (value: FieldValue) => {
      return validator
        .validate(value)
        .then(() => undefined)
        .catch(({ errors }: Yup.ValidationError) => errors[0]);
    },
    onBlur: () => {
      void validateField(name);
    },
  };
}
